import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Users, Plus, Trash2, Save, Edit2, Check, Clock, UserMinus } from "lucide-react";
import Button from "../UI/Button";
import Input from "../UI/Input";
import Toast from "../UI/Toast";
import {
  GuestEvent,
  CompanionCreateData,
  CompanionGuestEvent,
  CompanionGuestEventCreateData,
  CompanionGuestEventUpdateData,
  guestAPI,
} from "../../services/guestAPI";
import { useAppSelector } from "../../store/hooks";
import { logError } from "../../utils/errorLogger";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { PhoneInputWithValidation, PhoneValidationResponse } from "../UI/PhoneInputWithValidation";
import { useGuestDisplayName } from "../../hooks/useGuestDisplayName";
import { EmptyCompanionInput } from "../Companions/EmptyCompanionInput";

interface EditCompanionModalProps {
  isOpen: boolean;
  onClose: () => void;
  guest: GuestEvent | null;
  eventId: string;
  onUpdate: (closeModal?: boolean) => void;
  eventHasEnded?: boolean;
}

/**
 * Plain-English label for who recorded an RSVP.
 *
 * A companion with their own phone can be answered from two directions - by themselves,
 * and by the main guest confirming them - so the latest answer needs attributing.
 */
const describeAnswerSource = (source: string): string => {
  switch (source) {
    case "companion":
      return "themselves";
    case "primary_guest":
      return "the main guest";
    case "host":
      return "the host";
    case "admin":
      return "an admin";
    default:
      return source;
  }
};

const RELATIONSHIP_CHOICES = [
  { value: "spouse", label: "Spouse" },
  { value: "partner", label: "Partner" },
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "friend", label: "Friend" },
  { value: "colleague", label: "Colleague" },
  { value: "other", label: "Other" },
];

export const EditCompanionModal: React.FC<EditCompanionModalProps> = ({
  isOpen,
  onClose,
  guest,
  eventId,
  onUpdate,
  eventHasEnded = false,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  
  // Get display name for guest (nickname-aware)
  const guestId = guest?.guest?.id && typeof guest.guest.id === 'string' ? guest.guest.id : undefined;
  const { displayName } = useGuestDisplayName(guestId);
  
  const [companions, setCompanions] = useState<CompanionGuestEvent[]>([]);
  const [newCompanions, setNewCompanions] = useState<Array<CompanionCreateData & { first_name?: string; last_name?: string; nickname?: string }>>([]);
  
  // Helper function to convert name to first_name/last_name
  const splitName = (name: string): { first_name: string; last_name?: string } => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return { first_name: parts[0] };
    }
    return {
      first_name: parts[0],
      last_name: parts.slice(1).join(' ')
    };
  };
  const [loading, setLoading] = useState(false);
  const [fetchingCompanions, setFetchingCompanions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [allowCompanions, setAllowCompanions] = useState(false);
  const [companionLimit, setCompanionLimit] = useState<number | undefined>(
    undefined
  );
  const [emptyCompanionCount, setEmptyCompanionCount] = useState(0);
  const [emptyCompanionStatuses, setEmptyCompanionStatuses] = useState<Array<{index: number; status: 'pending' | 'accepted' | 'rejected' | 'ignored'}>>([]);
  const [companionStatusMenu, setCompanionStatusMenu] = useState<string | null>(null); // companionId for which status menu is open
  const [emptyCompanionStatusMenu, setEmptyCompanionStatusMenu] = useState<number | null>(null); // index of empty companion for which status menu is open

  // Companion history for selection
  const [companionHistory, setCompanionHistory] = useState<
    Array<{
      name: string;
      phone?: string;
      email?: string;
      relationship: string;
      last_used: string;
      event_count: number;
    }>
  >([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [selectedHistoryCompanions, setSelectedHistoryCompanions] = useState<
    Set<string>
  >(new Set());

  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingAllowCompanionsChange, setPendingAllowCompanionsChange] =
    useState<boolean | null>(null);

  // Companion addition confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCompanionsCount, setPendingCompanionsCount] = useState(0);
  const [isImporting, setIsImporting] = useState(false); // true if importing from history, false if adding new
  
  // Phone validation state for companions
  const [companionPhoneValidations, setCompanionPhoneValidations] = useState<Map<number, PhoneValidationResponse | null>>(new Map());
  
  // Ref to store stable callback functions for phone validation
  const validationCallbacksRef = useRef<Map<number, (validation: PhoneValidationResponse | null) => void>>(new Map());
  
  // Editing state for existing companions
  const [editingCompanionId, setEditingCompanionId] = useState<string | null>(null);
  const [editingCompanionData, setEditingCompanionData] = useState<{
    relationship: string;
    notes: string;
    phone?: string;
    email?: string;
  } | null>(null);
  const [editingCompanionValidation, setEditingCompanionValidation] = useState<PhoneValidationResponse | null>(null);

  // Handle scroll locking and escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, loading, onClose]);

  // Close status menu when clicking outside
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const emptyStatusMenuRef = useRef<HTMLDivElement>(null);
  const statusButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const emptyStatusButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Handle detailed companion status menu
      if (companionStatusMenu) {
        const button = statusButtonRefs.current.get(companionStatusMenu);
        if (statusMenuRef.current && !statusMenuRef.current.contains(target) &&
            button && !button.contains(target)) {
          setCompanionStatusMenu(null);
        }
      }
      
      // Handle empty companion status menu
      if (emptyCompanionStatusMenu !== null) {
        const button = emptyStatusButtonRefs.current.get(emptyCompanionStatusMenu);
        if (emptyStatusMenuRef.current && !emptyStatusMenuRef.current.contains(target) &&
            button && !button.contains(target)) {
          setEmptyCompanionStatusMenu(null);
        }
      }
    };

    if (companionStatusMenu || emptyCompanionStatusMenu !== null) {
      // Use a small delay to avoid closing immediately when opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [companionStatusMenu, emptyCompanionStatusMenu]);

  // Load guest data when modal opens
  useEffect(() => {
    if (isOpen && guest) {
      const initialAllowCompanions = guest.allow_companions || false;
      setAllowCompanions(initialAllowCompanions);
      setCompanionLimit(guest.companion_limit || undefined);
      setEmptyCompanionCount(guest.empty_companion_count || 0);
      setEmptyCompanionStatuses(guest.empty_companion_statuses || []);
      setNewCompanions([]);
      setError(null);
      setSuccess(null);
      setSelectedHistoryCompanions(new Set());
      setCompanionStatusMenu(null);
      setEmptyCompanionStatusMenu(null);

      // Only fetch companions if allow_companions is true
      const fetchCompanions = async () => {
        if (!guest || !eventId) return;

        // Only fetch companions if allow_companions is true
        if (initialAllowCompanions) {
          setFetchingCompanions(true);
          try {
            const fetchedCompanions = await guestAPI.companion.getCompanions(
              eventId,
              guest.id
            );
            setCompanions(fetchedCompanions || []);
          } catch (err: any) {
            logError("Failed to fetch companions", err);
          } finally {
            setFetchingCompanions(false);
          }
        } else {
          // If allow_companions is false, clear companions
          setCompanions([]);
        }
      };

      // Fetch companion history for selection
      const fetchHistory = async () => {
        if (!guest?.guest?.id) return;

        setFetchingHistory(true);
        try {
          const history = await guestAPI.getCompanionHistory(guest.guest.id);
          setCompanionHistory(history.companions || []);
        } catch (err: any) {
          logError("Failed to fetch companion history", err);
        } finally {
          setFetchingHistory(false);
        }
      };

      fetchCompanions();
      fetchHistory();
    }
  }, [isOpen, guest, eventId]);

  // Handle companion limit changes - clear selected companions if they exceed the new limit
  useEffect(() => {
    if (!companionLimit || companionLimit < 1) {
      // If limit is removed or invalid, clear all selections
      if (selectedHistoryCompanions.size > 0) {
        setSelectedHistoryCompanions(new Set());
      }
      return;
    }

    // Calculate total companions (existing + selected)
    const currentSelectedCount = selectedHistoryCompanions.size;
    const totalCompanions = companions.length + currentSelectedCount;

    // If total exceeds the new limit, clear excess selections
    if (totalCompanions > companionLimit) {
      const selectedArray = Array.from(selectedHistoryCompanions);
      const excessCount = totalCompanions - companionLimit;

      // Keep only the first (limit - existing) selections
      const allowedSelections = companionLimit - companions.length;
      if (allowedSelections > 0) {
        const newSelections = new Set(
          selectedArray.slice(0, allowedSelections)
        );
        setSelectedHistoryCompanions(newSelections);

        if (excessCount > 0) {
          setError(
            `Companion limit reduced to ${companionLimit}. ${excessCount} selection${
              excessCount !== 1 ? "s were" : " was"
            } automatically cleared.`
          );
        }
      } else {
        // No selections allowed if existing companions already meet/exceed limit
        const hadSelections = currentSelectedCount > 0;
        setSelectedHistoryCompanions(new Set());
        if (hadSelections) {
          setError(
            `Companion limit reduced to ${companionLimit}. All selections were cleared as you already have ${
              companions.length
            } companion${companions.length !== 1 ? "s" : ""}.`
          );
        }
      }
    }
  }, [companionLimit, companions.length, selectedHistoryCompanions]);

  // Auto-dismiss error and success messages
  useAutoDismiss(error, () => setError(null), 10000);
  useAutoDismiss(success, () => setSuccess(null), 10000);

  // Handle allow_companions change - delete companions if unchecked
  const handleAllowCompanionsChange = (checked: boolean) => {
    if (!checked && companions.length > 0) {
      // If unchecking and there are existing companions, show confirmation
      setPendingAllowCompanionsChange(checked);
      setShowDeleteConfirm(true);
    } else {
      // If no companions or checking, proceed immediately
      setAllowCompanions(checked);
      if (!checked) {
        setCompanions([]);
        setSelectedHistoryCompanions(new Set());
      }
    }
  };

  // Confirm deletion of companions
  const handleConfirmDeleteCompanions = async () => {
    if (pendingAllowCompanionsChange === null || !guest) return;

    setShowDeleteConfirm(false);
    setAllowCompanions(pendingAllowCompanionsChange);
    setPendingAllowCompanionsChange(null);

    setLoading(true);
    try {
      // Delete all existing companions
      for (const companion of companions) {
        try {
          await guestAPI.companion.deleteCompanion(
            eventId,
            guest.id,
            companion.id
          );
        } catch (err: any) {
          logError(`Failed to delete companion ${companion.id}`, err);
        }
      }
      setCompanions([]);
      setSelectedHistoryCompanions(new Set());
      setSuccess("Companions removed successfully");
    } catch (err: any) {
      setError("Failed to remove companions");
      setAllowCompanions(true); // Revert on error
    } finally {
      setLoading(false);
    }
  };

  // Cancel deletion
  const handleCancelDeleteCompanions = () => {
    setShowDeleteConfirm(false);
    setPendingAllowCompanionsChange(null);
  };

  // Handle empty companion count change
  const handleEmptyCompanionCountChange = async (count: number) => {
    if (!guest || eventHasEnded) return;
    
    setLoading(true);
    try {
      // Include companion_limit in the update so validation uses the current limit
      const updateData: any = {
        empty_companion_count: count
      };
      // Include companion_limit if it's set (either from local state or from guest)
      if (companionLimit !== undefined) {
        updateData.companion_limit = companionLimit;
      } else if (guest.companion_limit !== undefined) {
        updateData.companion_limit = guest.companion_limit;
      }
      
      await guestAPI.updateGuest(eventId, guest.id, updateData);
      setEmptyCompanionCount(count);
      setSuccess(`Empty companion count updated to ${count}`);
      // Don't call onUpdate() here to prevent modal from closing
      // The parent will refresh when modal is closed or when Save is clicked
    } catch (err: any) {
      logError("Failed to update empty companion count", err);
      setError(err.response?.data?.error?.message || "Failed to update empty companion count");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewCompanion = () => {
    if (eventHasEnded) {
      setError(
        "This event has already ended. Companion changes are not allowed for past events."
      );
      return;
    }

    // Validate that companion limit is set before adding
    if (!companionLimit || companionLimit < 1) {
      setError(
        "Please set a companion limit first. Enter a number in the 'Limit' field above."
      );
      return;
    }

    const currentTotal = companions.length + newCompanions.length;
    // Prevent adding if adding one more would exceed the limit
    if (companionLimit && currentTotal + 1 > companionLimit) {
      setError(
        `You've reached the maximum of ${companionLimit} companion${
          companionLimit !== 1 ? "s" : ""
        }. Please remove a companion first or increase the limit.`
      );
      return;
    }

    const newCompanion: CompanionCreateData = {
      name: "",
      phone: "",
      email: "",
      relationship: "friend",
      notes: "",
    };
    setNewCompanions((prev) => [...prev, newCompanion]);
    setError(null);
  };

  const handleCompanionChange = (
    index: number,
    field: keyof CompanionCreateData,
    value: string
  ) => {
    setNewCompanions((prev) =>
      prev.map((companion, i) =>
        i === index ? { ...companion, [field]: value } : companion
      )
    );
    // Phone validation is handled by PhoneInputWithValidation component
  };

  const handleCompanionPhoneValidationChange = useCallback((index: number, validation: PhoneValidationResponse | null) => {
    setCompanionPhoneValidations(prev => {
      // Check if the validation actually changed to prevent unnecessary updates
      const currentValidation = prev.get(index);
      
      // If both are null/undefined, no change
      if (!currentValidation && !validation) {
        return prev; // No change, return same reference to prevent re-render
      }
      
      // If one is null and the other isn't, there's a change
      if (!currentValidation || !validation) {
        const newMap = new Map(prev);
        newMap.set(index, validation);
        return newMap;
      }
      
      // Check if the validation data is the same (deep comparison for validation objects)
      if (currentValidation.valid === validation.valid &&
          currentValidation.normalized === validation.normalized &&
          currentValidation.error === validation.error &&
          currentValidation.country === validation.country) {
        return prev; // Same validation data, return same reference
      }
      
      const newMap = new Map(prev);
      newMap.set(index, validation);
      return newMap;
    });
  }, []);
  
  // Create stable callback functions for each companion index using ref
  const getValidationCallback = useCallback((index: number) => {
    if (!validationCallbacksRef.current.has(index)) {
      validationCallbacksRef.current.set(index, (validation: PhoneValidationResponse | null) => {
        handleCompanionPhoneValidationChange(index, validation);
      });
    }
    return validationCallbacksRef.current.get(index)!;
  }, [handleCompanionPhoneValidationChange]);
  
  // Stable callback for editing companion phone validation
  const handleEditingCompanionValidationChange = useCallback((validation: PhoneValidationResponse | null) => {
    setEditingCompanionValidation(prev => {
      // Check if validation actually changed to prevent unnecessary updates
      if (!prev && !validation) {
        return prev; // Both null, no change
      }
      if (!prev || !validation) {
        return validation; // One is null, there's a change
      }
      // Deep comparison
      if (prev.valid === validation.valid &&
          prev.normalized === validation.normalized &&
          prev.error === validation.error &&
          prev.country === validation.country) {
        return prev; // Same validation data, return same reference
      }
      return validation; // Different validation data
    });
  }, []);
  
  // Clean up callbacks when companions are removed
  useEffect(() => {
    // Remove callbacks for indices that no longer exist
    const maxIndex = newCompanions.length - 1;
    const indicesToRemove: number[] = [];
    validationCallbacksRef.current.forEach((_: (validation: PhoneValidationResponse | null) => void, index: number) => {
      if (index > maxIndex) {
        indicesToRemove.push(index);
      }
    });
    indicesToRemove.forEach((index: number) => {
      validationCallbacksRef.current.delete(index);
    });
  }, [newCompanions.length]);

  const handleRemoveNewCompanion = (index: number) => {
    setNewCompanions((prev) => prev.filter((_, i) => i !== index));
    // Clean up phone validation state for removed companion
    setCompanionPhoneValidations(prev => {
      const newMap = new Map(prev);
      // Shift all indices after the removed one
      const entries = Array.from(newMap.entries());
      newMap.clear();
      entries.forEach(([i, validation]) => {
        if (i < index) {
          newMap.set(i, validation);
        } else if (i > index) {
          newMap.set(i - 1, validation);
        }
      });
      return newMap;
    });
  };

  const handleEditCompanion = (companion: CompanionGuestEvent) => {
    if (eventHasEnded) {
      setError(
        "This event has already ended. You cannot edit companions from past events."
      );
      return;
    }
    setEditingCompanionId(companion.id);
    setEditingCompanionData({
      relationship: companion.relationship || 'friend',
      notes: companion.notes || '',
      phone: companion.guest_phone || '',
      email: companion.guest_email || '',
    });
    setEditingCompanionValidation(null);
  };

  const handleCancelEditCompanion = () => {
    setEditingCompanionId(null);
    setEditingCompanionData(null);
    setEditingCompanionValidation(null);
  };

  const handleSaveEditCompanion = async (companionId: string) => {
    if (!guest || !editingCompanionData) {
      console.error("Cannot save: missing guest or editingCompanionData", { guest, editingCompanionData });
      setError("Missing required data to save companion");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Validate phone if provided
      if (editingCompanionData.phone && editingCompanionData.phone.trim()) {
        if (!editingCompanionValidation || !editingCompanionValidation.valid) {
          setError("Please enter a valid phone number or remove it.");
          setLoading(false);
          return;
        }
      }

      const updateData: CompanionGuestEventUpdateData = {
        relationship: editingCompanionData.relationship as any,
        notes: editingCompanionData.notes || undefined,
        phone: editingCompanionData.phone && editingCompanionData.phone.trim() 
          ? (editingCompanionValidation?.normalized || editingCompanionData.phone.trim())
          : undefined,
        email: editingCompanionData.email && editingCompanionData.email.trim()
          ? editingCompanionData.email.trim()
          : undefined,
      };

      console.log("Updating companion:", { eventId, guestId: guest.id, companionId, updateData });

      const updatedCompanion = await guestAPI.companion.updateCompanion(eventId, guest.id, companionId, updateData);
      
      console.log("Companion updated successfully:", updatedCompanion);

      // Refresh companions list
      const fetchedCompanions = await guestAPI.companion.getCompanions(eventId, guest.id);
      setCompanions(fetchedCompanions || []);
      
      setEditingCompanionId(null);
      setEditingCompanionData(null);
      setEditingCompanionValidation(null);
      setSuccess("Companion updated successfully");
      
      // Call onUpdate to refresh parent component
      onUpdate();
    } catch (err: any) {
      console.error("Error updating companion:", err);
      logError("Failed to update companion", err);
      
      // Extract error message from various possible locations
      const errorMessage = 
        err.response?.data?.error?.message ||
        err.response?.data?.error?.details?.non_field_errors?.[0] ||
        (typeof err.response?.data?.error?.details === 'string' ? err.response.data.error.details : null) ||
        err.message ||
        "Failed to update companion";
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmptyCompanionStatusUpdate = async (index: number, newStatus: 'pending' | 'accepted' | 'rejected' | 'ignored', event?: React.MouseEvent) => {
    if (!guest || eventHasEnded) return;
    
    // Prevent event propagation to avoid closing modal
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setLoading(true);
    setError(null);
    setEmptyCompanionStatusMenu(null); // Close menu immediately
    
    try {
      // Get current statuses from local state and update the one at the specified index
      const currentStatuses = [...emptyCompanionStatuses];
      
      // Ensure the index exists
      if (index >= 0 && index < currentStatuses.length) {
        currentStatuses[index] = {
          ...currentStatuses[index],
          status: newStatus,
          index: index
        };
      } else {
        // If index doesn't exist, create it
        while (currentStatuses.length <= index) {
          currentStatuses.push({ index: currentStatuses.length, status: 'pending' });
        }
        currentStatuses[index] = { index: index, status: newStatus };
      }
      
      // Update the guest event with new statuses
      await guestAPI.updateGuest(eventId, guest.id, {
        empty_companion_statuses: currentStatuses
      } as any);
      
      // Update local state
      setEmptyCompanionStatuses(currentStatuses);
      
      // Update guest object for consistency
      if (guest) {
        guest.empty_companion_statuses = currentStatuses;
      }
      
      setSuccess(`Empty companion ${index + 1} status updated to ${newStatus}`);
      
      // Call onUpdate to refresh parent component (statistics) but don't close modal
      onUpdate(false);
    } catch (err: any) {
      logError("Failed to update empty companion status", err);
      setError(err.response?.data?.error?.message || "Failed to update empty companion status");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanionStatusUpdate = async (companionId: string, newStatus: 'pending' | 'accepted' | 'rejected' | 'ignored', event?: React.MouseEvent) => {
    if (!guest || eventHasEnded) return;
    
    // Prevent event propagation to avoid closing modal
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setLoading(true);
    setError(null);
    setCompanionStatusMenu(null); // Close menu immediately
    
    try {
      // Update companion status using the companion update endpoint
      await guestAPI.companion.updateCompanion(eventId, guest.id, companionId, {
        status: newStatus
      });
      
      // Refresh companions list
      const fetchedCompanions = await guestAPI.companion.getCompanions(eventId, guest.id);
      setCompanions(fetchedCompanions || []);
      
      setSuccess(`Companion status updated to ${newStatus}`);
      
      // Call onUpdate to refresh parent component (statistics) but don't close modal
      // Pass false to prevent modal from closing
      onUpdate(false);
    } catch (err: any) {
      logError("Failed to update companion status", err);
      setError(err.response?.data?.error?.message || "Failed to update companion status");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompanion = async (companionId: string) => {
    if (!guest) return;

    if (eventHasEnded) {
      setError(
        "This event has already ended. You cannot remove companions from past events."
      );
      return;
    }

    setLoading(true);
    try {
      await guestAPI.companion.deleteCompanion(eventId, guest.id, companionId);
      setCompanions((prev) => prev.filter((c) => c.id !== companionId));
      setSuccess("Companion deleted successfully");
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || "Failed to delete companion"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle importing companions from history - show confirmation first
  const handleImportFromHistory = async () => {
    if (!guest || selectedHistoryCompanions.size === 0) return;

    if (eventHasEnded) {
      setError(
        "This event has already ended. You cannot import companions for past events."
      );
      return;
    }

    // Validate that companion limit is set before importing
    if (!companionLimit || companionLimit < 1) {
      setError(
        "Please set a companion limit first. Enter a number in the 'Limit' field above."
      );
      return;
    }

    // Calculate how many companions will actually be imported (excluding already added ones)
    const companionsToImport = Array.from(selectedHistoryCompanions).filter(
      (name) => !companions.some((c) => (c.display_name || c.guest_name) === name)
    );

    if (companionsToImport.length === 0) {
      setError("All selected companions are already added.");
      return;
    }

    // Show confirmation modal
    setPendingCompanionsCount(companionsToImport.length);
    setIsImporting(true);
    setShowConfirmModal(true);
  };

  // Actually perform the import after confirmation
  const handleConfirmImport = async () => {
    if (!guest || selectedHistoryCompanions.size === 0) return;

    setShowConfirmModal(false);
    setLoading(true);
    setError(null);

    try {
      // First, update guest event settings to ensure allow_companions and limit are saved
      // This is required because the backend validates allow_companions before allowing companion creation
      await guestAPI.updateGuest(eventId, guest.id, {
        allow_companions: allowCompanions,
        companion_limit: companionLimit,
      });

      // Refresh companions list to get accurate count from backend
      const currentCompanions = await guestAPI.companion.getCompanions(
        eventId,
        guest.id
      );
      setCompanions(currentCompanions || []);

      const validRelationships = [
        "spouse",
        "partner",
        "child",
        "parent",
        "sibling",
        "friend",
        "colleague",
        "relative",
        "other",
      ] as const;
      type ValidRelationship = (typeof validRelationships)[number];

      // Create a map of companion names to their data
      const companionMap = new Map<string, (typeof companionHistory)[0]>();
      companionHistory.forEach((comp) => {
        companionMap.set(comp.name, comp);
      });

      const importErrors: string[] = [];
      let importedCount = 0;
      let currentCompanionCount = currentCompanions?.length || 0;

      for (const companionName of Array.from(selectedHistoryCompanions)) {
        const historyCompanion = companionMap.get(companionName);
        if (!historyCompanion) continue;

        // Check if already exists (using refreshed list)
        const exists = currentCompanions?.some((c) => (c.display_name || c.guest_name) === companionName);
        if (exists) continue;

        // Check limit dynamically - account for companions already added in this batch
        if (companionLimit && currentCompanionCount >= companionLimit) {
          setError(
            `You've reached your companion limit of ${companionLimit}. Please remove an existing companion before importing more.`
          );
          break;
        }

        const relationship: ValidRelationship = validRelationships.includes(
          historyCompanion.relationship as ValidRelationship
        )
          ? (historyCompanion.relationship as ValidRelationship)
          : "other";

        try {
          const nameParts = splitName(historyCompanion.name);
          await guestAPI.companion.createCompanion(eventId, guest.id, {
            first_name: nameParts.first_name,
            last_name: nameParts.last_name,
            phone: historyCompanion.phone || undefined,
            email: historyCompanion.email || undefined,
            relationship: relationship,
            notes: "",
          });
          importedCount++;
          currentCompanionCount++; // Increment local count as we add companions
        } catch (companionErr: any) {
          logError(
            `Failed to import companion ${historyCompanion.name}`,
            companionErr
          );
          const errorMessage =
            companionErr.response?.data?.error?.message ||
            "Failed to import companion";
          importErrors.push(`${historyCompanion.name}: ${errorMessage}`);
          // If limit reached error, stop trying to import more
          if (
            errorMessage.includes("limit") ||
            errorMessage.includes("Limit")
          ) {
            break;
          }
        }
      }

      // Refresh companions list to show final state
      const fetchedCompanions = await guestAPI.companion.getCompanions(
        eventId,
        guest.id
      );
      setCompanions(fetchedCompanions || []);
      setSelectedHistoryCompanions(new Set());

      // Show appropriate message based on results
      if (importErrors.length > 0) {
        if (importedCount > 0) {
          setError(
            `Partially imported: ${importedCount} companion${
              importedCount !== 1 ? "s" : ""
            } imported, but ${importErrors.length} failed. ${importErrors.join(
              "; "
            )}`
          );
        } else {
          setError(`Failed to import companions: ${importErrors.join("; ")}`);
        }
      } else {
        setSuccess("Companions imported successfully");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message || "Failed to import companions";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle save - show confirmation if there are new companions to add
  const handleSave = async () => {
    if (!guest) return;

    if (eventHasEnded) {
      setError(
        "This event has already ended. Companion settings cannot be changed for past events."
      );
      return;
    }

    // Count valid new companions (with names)
    const validNewCompanions = newCompanions.filter((c) => c.name.trim());
    
    // Validate phone numbers for all companions
    const phoneValidationErrors: string[] = [];
    validNewCompanions.forEach((companion, index) => {
      if (companion.phone && companion.phone.trim()) {
        const validation = companionPhoneValidations.get(index);
        if (!validation || !validation.valid) {
          phoneValidationErrors.push(`${companion.name || companion.first_name || `Companion ${index + 1}`}: ${validation?.error || 'Please enter a valid phone number'}`);
        }
      }
    });
    
    if (phoneValidationErrors.length > 0) {
      setError(phoneValidationErrors.join('; '));
      return;
    }

    // If there are new companions to add, show confirmation modal
    if (validNewCompanions.length > 0) {
      setPendingCompanionsCount(validNewCompanions.length);
      setIsImporting(false);
      setShowConfirmModal(true);
      return;
    }

    // No new companions, just update settings
    setLoading(true);
    setError(null);

    try {
      // Update guest event settings
      await guestAPI.updateGuest(eventId, guest.id, {
        allow_companions: allowCompanions,
        companion_limit: companionLimit,
      });

      setSuccess("Companion information updated successfully");
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || "Failed to update companions"
      );
    } finally {
      setLoading(false);
    }
  };

  // Actually perform the save after confirmation
  const handleConfirmSave = async () => {
    if (!guest) return;

    setShowConfirmModal(false);
    setLoading(true);
    setError(null);

    try {
      // Update guest event settings
      await guestAPI.updateGuest(eventId, guest.id, {
        allow_companions: allowCompanions,
        companion_limit: companionLimit,
      });

      // Refresh companions list to get accurate count from backend
      const currentCompanions = await guestAPI.companion.getCompanions(
        eventId,
        guest.id
      );
      setCompanions(currentCompanions || []);

      let currentCompanionCount = currentCompanions?.length || 0;
      const saveErrors: string[] = [];
      let savedCount = 0;

      // Create new companions
      for (const companionData of newCompanions) {
        if (!companionData.name.trim()) continue;

        // Check limit dynamically
        if (companionLimit && currentCompanionCount >= companionLimit) {
          setError(
            `You've reached your companion limit of ${companionLimit}. Please remove an existing companion before adding more.`
          );
          break;
        }

        try {
          // Use normalized phone from validation if available
          const companionIndex = newCompanions.findIndex(c => c === companionData);
          const validation = companionPhoneValidations.get(companionIndex);
          
          let phone: string | undefined = undefined;
          if (companionData.phone && companionData.phone.trim()) {
            if (validation && validation.valid) {
              phone = validation.normalized;
            } else {
              // Skip this companion if phone is invalid
              saveErrors.push(`${companionData.name || 'Companion'}: Invalid phone number`);
              continue;
            }
          }
          
          const nameParts = splitName(companionData.name);
          const createData: CompanionGuestEventCreateData = {
            first_name: nameParts.first_name,
            last_name: nameParts.last_name,
            phone: phone,
            email: companionData.email || undefined,
            relationship: companionData.relationship,
            notes: companionData.notes || undefined,
            nickname: companionData.nickname || undefined,
          };
          
          await guestAPI.companion.createCompanion(
            eventId,
            guest.id,
            createData
          );
          savedCount++;
          currentCompanionCount++; // Increment local count as we add companions
        } catch (companionErr: any) {
          logError(
            `Failed to add companion ${companionData.name}`,
            companionErr
          );
          // Extract error message - check multiple possible locations
          const errorMessage =
            companionErr.response?.data?.error?.message ||
            companionErr.response?.data?.error?.details?.[0] ||
            companionErr.message ||
            "Failed to add companion";
          saveErrors.push(`${companionData.name}: ${errorMessage}`);
          // If limit reached error, stop trying to add more
          if (
            errorMessage.includes("limit") ||
            errorMessage.includes("Limit")
          ) {
            break;
          }
        }
      }

      // Refresh companions list to show final state
      const fetchedCompanions = await guestAPI.companion.getCompanions(
        eventId,
        guest.id
      );
      setCompanions(fetchedCompanions || []);

      // Show appropriate message
      if (saveErrors.length > 0) {
        if (savedCount > 0) {
          setError(
            `Partially saved: ${savedCount} companion${
              savedCount !== 1 ? "s" : ""
            } added, but ${saveErrors.length} failed. ${saveErrors.join("; ")}`
          );
        } else {
          setError(`Failed to add companions: ${saveErrors.join("; ")}`);
        }
      } else {
        setSuccess("Companion information updated successfully");
        onUpdate();
        onClose();
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message || "Failed to update companions"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCompanions([]);
    setNewCompanions([]);
    setError(null);
    setSuccess(null);
    setCompanionHistory([]);
    setSelectedHistoryCompanions(new Set());
    setShowConfirmModal(false);
    setPendingCompanionsCount(0);
    onClose();
  };

  if (!isOpen || !guest) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!loading && e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal Container */}
      <div 
        className="relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain"
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div
          className={`${
            theme === "dark" ? "bg-gray-800" : "bg-white"
          } rounded-lg shadow-xl overflow-hidden flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0 ${
            theme === "dark" ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center min-w-0 flex-1">
            <Users
              className={`h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 ${
                theme === "dark" ? "text-purple-400" : "text-purple-600"
              } mr-2 sm:mr-3`}
            />
            <h2
              className={`text-base sm:text-xl font-semibold truncate ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Edit Companions - {displayName || guest?.guest_name || (guest?.guest?.first_name && guest?.guest?.last_name ? `${guest.guest.first_name} ${guest.guest.last_name}` : 'Guest')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
              theme === "dark" 
                ? "text-gray-400 hover:text-gray-300 hover:bg-gray-700" 
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Toast Messages - Fixed position above scrollable content */}
        {(error || success) && (
          <div className="px-4 sm:px-6 pt-4 pb-0 flex-shrink-0">
            {error && (
              <div className="mb-3">
                <Toast
                  message={error}
                  type="error"
                  onClose={() => setError(null)}
                />
              </div>
            )}
            {success && (
              <div className="mb-3">
                <Toast
                  message={success}
                  type="success"
                  onClose={() => setSuccess(null)}
                />
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-4 sm:pb-6">
          {/* Event Ended Warning */}
          {eventHasEnded && (
            <div
              className={`mb-6 p-4 ${
                theme === "dark"
                  ? "bg-yellow-900/20 border-yellow-800"
                  : "bg-yellow-50 border-yellow-200"
              } border rounded-lg`}
            >
              <p
                className={`text-sm font-medium ${
                  theme === "dark" ? "text-yellow-300" : "text-yellow-800"
                }`}
              >
                ⚠️ This event has already ended. You cannot edit companions for
                ended events.
              </p>
            </div>
          )}

          {/* Companion Settings */}
          <div className="mb-6 pt-4">
            <div className="flex items-center space-x-4 mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={allowCompanions}
                  onChange={(e) =>
                    handleAllowCompanionsChange(e.target.checked)
                  }
                  disabled={eventHasEnded || loading}
                  className="mr-2"
                />
                <span
                  className={`text-sm font-medium ${
                    theme === "dark" ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Allow Companions
                </span>
              </label>

              {allowCompanions && (
                <div className="flex items-center space-x-2">
                  <label
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Limit:
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={companionLimit || ""}
                    onChange={(e) =>
                      setCompanionLimit(
                        e.target.value ? parseInt(e.target.value) : undefined
                      )
                    }
                    disabled={eventHasEnded}
                    className={`w-20 px-2 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                      theme === "dark"
                        ? "border-gray-600 bg-gray-700 text-white"
                        : "border-gray-300 bg-white text-gray-900"
                    }`}
                  />
                </div>
              )}
            </div>

            {/* Empty Companions Input */}
            {allowCompanions && (
              <div className="mt-4">
                <EmptyCompanionInput
                  value={emptyCompanionCount}
                  onChange={handleEmptyCompanionCountChange}
                  companionLimit={companionLimit}
                  detailedCompanionCount={companions.length}
                  disabled={eventHasEnded || loading}
                  label="Add empty companions (count only, no details required)"
                />
              </div>
            )}
          </div>

          {/* Existing Companions - Only show if allow_companions is true */}
          {allowCompanions && (
            <div className="mb-6">
              <h3
                className={`text-lg font-medium mb-3 ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Current Companions ({companions.length}{emptyCompanionCount > 0 ? ` + ${emptyCompanionCount} empty` : ''})
              </h3>
              {emptyCompanionCount > 0 && (
                <div className={`mb-3 p-3 rounded-lg ${
                  theme === "dark" ? "bg-gray-800 border border-gray-700" : "bg-gray-50 border border-gray-200"
                }`}>
                  <p className={`text-sm font-medium mb-2 ${
                    theme === "dark" ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <strong>{emptyCompanionCount}</strong> empty companion{emptyCompanionCount !== 1 ? 's' : ''} (no details provided)
                  </p>
                  {emptyCompanionStatuses.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {emptyCompanionStatuses.map((statusObj: any, idx: number) => (
                        <div key={idx} className="inline-block mr-2 mb-1">
                          <div className="relative">
                            <button
                              ref={(el) => {
                                if (el) {
                                  emptyStatusButtonRefs.current.set(idx, el);
                                } else {
                                  emptyStatusButtonRefs.current.delete(idx);
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (!eventHasEnded && !loading) {
                                  setEmptyCompanionStatusMenu(
                                    emptyCompanionStatusMenu === idx ? null : idx
                                  );
                                }
                              }}
                              disabled={eventHasEnded || loading}
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-all duration-200 hover:scale-105 ${
                                statusObj.status === 'accepted'
                                  ? theme === "dark"
                                    ? "bg-green-900/30 text-green-300 border border-green-700/50"
                                    : "bg-green-100 text-green-700 border border-green-200"
                                  : statusObj.status === 'rejected'
                                  ? theme === "dark"
                                    ? "bg-red-900/30 text-red-300 border border-red-700/50"
                                    : "bg-red-100 text-red-700 border border-red-200"
                                  : statusObj.status === 'pending'
                                  ? theme === "dark"
                                    ? "bg-yellow-900/30 text-yellow-300 border border-yellow-700/50"
                                    : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                  : theme === "dark"
                                  ? "bg-gray-700 text-gray-400 border border-gray-600"
                                  : "bg-gray-100 text-gray-600 border border-gray-200"
                              } ${eventHasEnded || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              title={eventHasEnded ? "Cannot change status for past events" : "Click to change status"}
                            >
                              {statusObj.status === 'accepted' && <Check className="h-3 w-3 mr-1" />}
                              {statusObj.status === 'rejected' && <X className="h-3 w-3 mr-1" />}
                              {statusObj.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {statusObj.status === 'ignored' && <UserMinus className="h-3 w-3 mr-1" />}
                              Companion {idx + 1}: {statusObj.status === 'accepted' ? 'Confirmed' : 
                                                   statusObj.status === 'rejected' ? 'Declined' :
                                                   statusObj.status === 'pending' ? 'Pending' : 'No Response'}
                            </button>
                            
                            {emptyCompanionStatusMenu === idx && !eventHasEnded && (
                              <div 
                                ref={emptyStatusMenuRef}
                                className="absolute left-0 top-full mt-1 w-48 rounded-lg shadow-lg border py-1 z-[100] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                {[
                                  { value: 'pending', label: 'Mark as Pending', icon: Clock, color: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600' },
                                  { value: 'accepted', label: 'Mark as Confirmed', icon: Check, color: theme === 'dark' ? 'text-green-400' : 'text-green-600' },
                                  { value: 'rejected', label: 'Mark as Declined', icon: X, color: theme === 'dark' ? 'text-red-400' : 'text-red-600' },
                                  { value: 'ignored', label: 'Mark as No Response', icon: UserMinus, color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600' }
                                ].map((option) => {
                                  const Icon = option.icon;
                                  return (
                                    <button
                                      key={option.value}
                                      onClick={(e) => handleEmptyCompanionStatusUpdate(idx, option.value as any, e)}
                                      disabled={statusObj.status === option.value || loading}
                                      className={`flex items-center w-full px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${option.color} ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                      }`}
                                    >
                                      <Icon className="h-4 w-4 mr-2" />
                                      {option.label}
                                      {statusObj.status === option.value && (
                                        <Check className={`h-3 w-3 ml-auto ${
                                          theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                        }`} />
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {fetchingCompanions ? (
                <div
                  className={`text-sm ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Loading companions...
                </div>
              ) : companions.length > 0 ? (
                <div className="space-y-3">
                  {companions.map((companion) => (
                    <div
                      key={companion.id}
                      className={`p-3 border rounded-lg ${
                        theme === "dark" ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      {editingCompanionId === companion.id ? (
                        // Edit mode
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4
                              className={`font-medium ${
                                theme === "dark" ? "text-white" : "text-gray-900"
                              }`}
                            >
                              Edit: {companion.display_name || companion.guest_name}
                            </h4>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  await handleSaveEditCompanion(companion.id);
                                }}
                                disabled={loading}
                                className={`px-3 py-1 text-sm rounded ${
                                  loading
                                    ? "opacity-50 cursor-not-allowed"
                                    : theme === "dark"
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-green-500 hover:bg-green-600 text-white"
                                }`}
                              >
                                {loading ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleCancelEditCompanion();
                                }}
                                disabled={loading}
                                className={`px-3 py-1 text-sm rounded ${
                                  loading
                                    ? "opacity-50 cursor-not-allowed"
                                    : theme === "dark"
                                    ? "bg-gray-600 hover:bg-gray-700 text-white"
                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                }`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label
                                className={`block text-sm font-medium mb-1 ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Relationship
                              </label>
                              <select
                                value={editingCompanionData?.relationship || 'friend'}
                                onChange={(e) =>
                                  setEditingCompanionData((prev) =>
                                    prev ? { ...prev, relationship: e.target.value } : null
                                  )
                                }
                                disabled={loading || eventHasEnded}
                                className={`w-full px-3 py-2 border rounded ${
                                  theme === "dark"
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                              >
                                {RELATIONSHIP_CHOICES.map((choice) => (
                                  <option key={choice.value} value={choice.value}>
                                    {choice.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label
                                className={`block text-sm font-medium mb-1 ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Phone
                              </label>
                              <PhoneInputWithValidation
                                value={editingCompanionData?.phone || ''}
                                onChange={(phoneValue) =>
                                  setEditingCompanionData((prev) =>
                                    prev ? { ...prev, phone: phoneValue } : null
                                  )
                                }
                                onValidationChange={handleEditingCompanionValidationChange}
                                showValidationResult={false}
                                placeholder="Enter phone number (optional)"
                                disabled={eventHasEnded || loading}
                              />
                            </div>

                            <div>
                              <label
                                className={`block text-sm font-medium mb-1 ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Email
                              </label>
                              <Input
                                type="email"
                                value={editingCompanionData?.email || ''}
                                onChange={(e) =>
                                  setEditingCompanionData((prev) =>
                                    prev ? { ...prev, email: e.target.value } : null
                                  )
                                }
                                placeholder="Enter email address"
                                disabled={eventHasEnded || loading}
                              />
                            </div>

                            <div className="md:col-span-2">
                              <label
                                className={`block text-sm font-medium mb-1 ${
                                  theme === "dark"
                                    ? "text-gray-300"
                                    : "text-gray-700"
                                }`}
                              >
                                Notes
                              </label>
                              <textarea
                                value={editingCompanionData?.notes || ''}
                                onChange={(e) =>
                                  setEditingCompanionData((prev) =>
                                    prev ? { ...prev, notes: e.target.value } : null
                                  )
                                }
                                disabled={eventHasEnded || loading}
                                rows={3}
                                className={`w-full px-3 py-2 border rounded ${
                                  theme === "dark"
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                                placeholder="Add any notes about this companion"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div
                                className={`font-medium ${
                                  theme === "dark" ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {companion.display_name || companion.guest_name}
                              </div>
                              {companion.nickname && (
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    theme === "dark"
                                      ? "bg-purple-900/30 text-purple-300"
                                      : "bg-purple-100 text-purple-700"
                                  }`}
                                >
                                  {companion.nickname}
                                </span>
                              )}
                              <div className="relative">
                                <button
                                  ref={(el) => {
                                    if (el) {
                                      statusButtonRefs.current.set(companion.id, el);
                                    } else {
                                      statusButtonRefs.current.delete(companion.id);
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (!eventHasEnded && !loading) {
                                      setCompanionStatusMenu(
                                        companionStatusMenu === companion.id ? null : companion.id
                                      );
                                    }
                                  }}
                                  disabled={eventHasEnded || loading}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 hover:scale-105 ${
                                    companion.status === "accepted"
                                      ? theme === "dark"
                                        ? "bg-green-900/30 text-green-300 border border-green-700/50"
                                        : "bg-green-100 text-green-700 border border-green-200"
                                      : companion.status === "rejected"
                                      ? theme === "dark"
                                        ? "bg-red-900/30 text-red-300 border border-red-700/50"
                                        : "bg-red-100 text-red-700 border border-red-200"
                                      : companion.status === "pending"
                                      ? theme === "dark"
                                        ? "bg-yellow-900/30 text-yellow-300 border border-yellow-700/50"
                                        : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                      : theme === "dark"
                                      ? "bg-gray-700 text-gray-400 border border-gray-600"
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                                  } ${eventHasEnded || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  title={eventHasEnded ? "Cannot change status for past events" : "Click to change status"}
                                >
                                  {companion.status === "accepted" && <Check className="h-3 w-3 mr-1" />}
                                  {companion.status === "rejected" && <X className="h-3 w-3 mr-1" />}
                                  {companion.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                                  {companion.status === "ignored" && <UserMinus className="h-3 w-3 mr-1" />}
                                  {companion.status === "accepted" ? "Confirmed" : 
                                   companion.status === "rejected" ? "Declined" :
                                   companion.status === "pending" ? "Pending" : "No Response"}
                                </button>
                                
                                {companionStatusMenu === companion.id && !eventHasEnded && (
                                  <div 
                                    ref={statusMenuRef}
                                    className="absolute left-0 top-full mt-1 w-48 rounded-lg shadow-lg border py-1 z-[100] bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                    }}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                    }}
                                  >
                                    {[
                                      { value: 'pending', label: 'Mark as Pending', icon: Clock, color: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600' },
                                      { value: 'accepted', label: 'Mark as Confirmed', icon: Check, color: theme === 'dark' ? 'text-green-400' : 'text-green-600' },
                                      { value: 'rejected', label: 'Mark as Declined', icon: X, color: theme === 'dark' ? 'text-red-400' : 'text-red-600' },
                                      { value: 'ignored', label: 'Mark as No Response', icon: UserMinus, color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600' }
                                    ].map((option) => {
                                      const Icon = option.icon;
                                      return (
                                        <button
                                          key={option.value}
                                          onClick={(e) => handleCompanionStatusUpdate(companion.id, option.value as any, e)}
                                          disabled={companion.status === option.value || loading}
                                          className={`flex items-center w-full px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${option.color} ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                          }`}
                                        >
                                          <Icon className="h-4 w-4 mr-2" />
                                          {option.label}
                                          {companion.status === option.value && (
                                            <Check className={`h-3 w-3 ml-auto ${
                                              theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                            }`} />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div
                              className={`text-sm ${
                                theme === "dark" ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {companion.relationship}
                              {companion.guest_phone && ` • ${companion.guest_phone}`}
                              {companion.guest_email && ` • ${companion.guest_email}`}
                              {companion.answered_by && (
                                <>
                                  {" • "}
                                  <span
                                    title={
                                      companion.answered_by.previous?.length
                                        ? `Replaced an earlier "${companion.answered_by.previous[0].status}" from ${describeAnswerSource(companion.answered_by.previous[0].source)}${companion.answered_by.previous[0].name ? ` (${companion.answered_by.previous[0].name})` : ""}`
                                        : undefined
                                    }
                                    className={
                                      companion.answered_by.previous?.length
                                        ? theme === "dark"
                                          ? "text-amber-400"
                                          : "text-amber-700"
                                        : undefined
                                    }
                                  >
                                    Answered by {describeAnswerSource(companion.answered_by.source)}
                                    {companion.answered_by.name ? ` (${companion.answered_by.name})` : ""}
                                    {companion.answered_by.previous?.length ? " — replaced an earlier reply" : ""}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditCompanion(companion)}
                              disabled={loading || eventHasEnded}
                              className={`p-1 ${
                                eventHasEnded
                                  ? "opacity-50 cursor-not-allowed text-gray-400"
                                  : theme === "dark"
                                  ? "text-blue-400 hover:text-blue-300"
                                  : "text-blue-600 hover:text-blue-800"
                              }`}
                              title={
                                eventHasEnded
                                  ? "This event has already ended. You cannot edit companions from past events."
                                  : "Edit companion"
                              }
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCompanion(companion.id)}
                              disabled={loading || eventHasEnded}
                              className={`p-1 ${
                                eventHasEnded
                                  ? "opacity-50 cursor-not-allowed text-gray-400"
                                  : theme === "dark"
                                  ? "text-red-400 hover:text-red-300"
                                  : "text-red-600 hover:text-red-800"
                              }`}
                              title={
                                eventHasEnded
                                  ? "This event has already ended. You cannot remove companions from past events."
                                  : "Delete companion"
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className={`p-4 border rounded-lg text-center ${
                    theme === "dark"
                      ? "border-gray-600 bg-gray-700/50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    No companions added yet for this event.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Companion History - Show available companions from previous events */}
          {!eventHasEnded && allowCompanions && companionHistory.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3
                  className={`text-lg font-medium ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Available from Previous Events ({companionHistory.length})
                </h3>
                {selectedHistoryCompanions.size > 0 &&
                  (() => {
                    const totalWithSelected =
                      companions.length + selectedHistoryCompanions.size;
                    const wouldExceedLimit = Boolean(
                      companionLimit && totalWithSelected > companionLimit
                    );
                    const isDisabled =
                      loading ||
                      !companionLimit ||
                      companionLimit < 1 ||
                      wouldExceedLimit;

                    return (
                      <Button
                        onClick={handleImportFromHistory}
                        disabled={isDisabled}
                        className="text-sm"
                        title={
                          wouldExceedLimit
                            ? `Cannot import: would exceed limit of ${companionLimit} (${companions.length} existing + ${selectedHistoryCompanions.size} selected)`
                            : undefined
                        }
                      >
                        Import Selected ({selectedHistoryCompanions.size})
                      </Button>
                    );
                  })()}
              </div>

              {fetchingHistory ? (
                <div
                  className={`text-sm ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Loading companion history...
                </div>
              ) : (
                <div
                  className={`space-y-2 max-h-48 overflow-y-auto border rounded p-2 ${
                    theme === "dark" ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  {companionHistory.map((historyCompanion) => {
                    const isAlreadyAdded = companions.some(
                      (c) => (c.display_name || c.guest_name) === historyCompanion.name ||
                        (c.guest_phone && historyCompanion.phone && c.guest_phone === historyCompanion.phone)
                    );
                    const isSelected = selectedHistoryCompanions.has(
                      historyCompanion.name
                    );

                    return (
                      <div
                        key={historyCompanion.name}
                        className={`flex items-center gap-3 p-3 border rounded-lg ${
                          isAlreadyAdded
                            ? theme === "dark"
                              ? "border-gray-700 bg-gray-800/50 opacity-50"
                              : "border-gray-200 bg-gray-100 opacity-50"
                            : isSelected
                            ? theme === "dark"
                              ? "border-blue-600 bg-blue-900/30"
                              : "border-blue-300 bg-blue-50"
                            : theme === "dark"
                            ? "border-gray-600 bg-gray-700/30"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isAlreadyAdded) return;

                            // Check if limit is set before allowing selection
                            if (!companionLimit || companionLimit < 1) {
                              setError(
                                "Please set a companion limit first. Enter a number in the 'Limit' field above."
                              );
                              return;
                            }

                            setSelectedHistoryCompanions((prev) => {
                              const newSet = new Set(prev);
                              if (newSet.has(historyCompanion.name)) {
                                newSet.delete(historyCompanion.name);
                              } else {
                                // Check limit
                                const currentTotal =
                                  companions.length + newSet.size;
                                if (
                                  companionLimit &&
                                  currentTotal + 1 > companionLimit
                                ) {
                                  setError(
                                    `You can only select up to ${companionLimit} companion${
                                      companionLimit !== 1 ? "s" : ""
                                    }. Please remove a selection or increase the limit above.`
                                  );
                                  return prev;
                                }
                                newSet.add(historyCompanion.name);
                              }
                              return newSet;
                            });
                            setError(null);
                          }}
                          disabled={
                            isAlreadyAdded ||
                            eventHasEnded ||
                            loading ||
                            !companionLimit ||
                            companionLimit < 1
                          }
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <div
                            className={`font-medium ${
                              theme === "dark" ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {historyCompanion.name}
                            {isAlreadyAdded && (
                              <span
                                className={`ml-2 text-xs ${
                                  theme === "dark"
                                    ? "text-gray-500"
                                    : "text-gray-500"
                                }`}
                              >
                                (Already added)
                              </span>
                            )}
                          </div>
                          <div
                            className={`text-sm ${
                              theme === "dark"
                                ? "text-gray-400"
                                : "text-gray-600"
                            }`}
                          >
                            {historyCompanion.relationship}
                            {historyCompanion.phone &&
                              ` • ${historyCompanion.phone}`}
                            {historyCompanion.email &&
                              ` • ${historyCompanion.email}`}
                            {historyCompanion.event_count > 0 && (
                              <span className="ml-2">
                                • Used in {historyCompanion.event_count} event
                                {historyCompanion.event_count !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Add New Companions */}
          {!eventHasEnded &&
            allowCompanions &&
            companionLimit &&
            companions.length + newCompanions.length <= companionLimit && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3
                    className={`text-lg font-medium ${
                      theme === "dark" ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Add New Companions
                  </h3>
                  {companions.length + newCompanions.length <
                    companionLimit && (
                    <button
                      onClick={handleAddNewCompanion}
                      disabled={eventHasEnded}
                      className={`flex items-center px-3 py-2 text-sm rounded-lg ${
                        eventHasEnded
                          ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                          : "bg-purple-600 text-white hover:bg-purple-700"
                      }`}
                      title={
                        eventHasEnded
                          ? "This event has already ended. Companion changes are not allowed for past events."
                          : ""
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Companion
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {newCompanions.map((companion, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded-lg ${
                        theme === "dark" ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4
                          className={`font-medium ${
                            theme === "dark" ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Companion {index + 1}
                        </h4>
                        <button
                          onClick={() => handleRemoveNewCompanion(index)}
                          disabled={eventHasEnded}
                          className={`${
                            eventHasEnded
                              ? "opacity-50 cursor-not-allowed text-gray-400"
                              : theme === "dark"
                              ? "text-red-400 hover:text-red-300"
                              : "text-red-600 hover:text-red-800"
                          }`}
                          title={
                            eventHasEnded
                              ? "Cannot remove companions for events that have already ended"
                              : ""
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Name *
                          </label>
                          <Input
                            value={companion.name}
                            onChange={(e) =>
                              handleCompanionChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                            placeholder="Enter companion name"
                            required
                            disabled={eventHasEnded}
                          />
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Relationship
                          </label>
                          <select
                            value={companion.relationship}
                            onChange={(e) =>
                              handleCompanionChange(
                                index,
                                "relationship",
                                e.target.value
                              )
                            }
                            disabled={eventHasEnded}
                            className={`w-full px-3 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                              theme === "dark"
                                ? "border-gray-600 bg-gray-700 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                          >
                            {RELATIONSHIP_CHOICES.map((choice) => (
                              <option key={choice.value} value={choice.value}>
                                {choice.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Phone
                          </label>
                          <PhoneInputWithValidation
                            value={companion.phone || ''}
                            onChange={(phoneValue, countryCodeValue) => {
                              handleCompanionChange(index, "phone", phoneValue);
                            }}
                            onValidationChange={getValidationCallback(index)}
                            showValidationResult={false}
                            placeholder="Enter phone number (optional)"
                            disabled={eventHasEnded}
                            dropdownZIndex={10001}
                          />
                        </div>

                        <div>
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Email
                          </label>
                          <Input
                            type="email"
                            value={companion.email}
                            onChange={(e) =>
                              handleCompanionChange(
                                index,
                                "email",
                                e.target.value
                              )
                            }
                            placeholder="Enter email address"
                            disabled={eventHasEnded}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label
                            className={`block text-sm font-medium mb-1 ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Notes
                          </label>
                          <textarea
                            value={companion.notes}
                            onChange={(e) =>
                              handleCompanionChange(
                                index,
                                "notes",
                                e.target.value
                              )
                            }
                            placeholder="Additional notes (optional)"
                            rows={2}
                            disabled={eventHasEnded}
                            className={`w-full px-3 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                              theme === "dark"
                                ? "border-gray-600 bg-gray-700 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Summary */}
          <div
            className={`p-4 rounded-lg ${
              theme === "dark" ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div
              className={`text-sm ${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}
            >
              <strong>Summary:</strong> {companions.length} existing companion
              {companions.length !== 1 ? "s" : ""}
              {newCompanions.length > 0 && (
                <span>
                  , {newCompanions.length} new companion
                  {newCompanions.length !== 1 ? "s" : ""} to add
                </span>
              )}
              {companionLimit && <span> (Limit: {companionLimit})</span>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end space-x-3 p-6 border-t flex-shrink-0 ${
            theme === "dark" ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <Button onClick={handleClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || eventHasEnded}
            className={`flex items-center ${
              eventHasEnded ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={
              eventHasEnded
                ? "This event has already ended. Companion settings cannot be changed for past events."
                : ""
            }
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
      </div>

      {/* Delete Companions Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
          <div
            className={`${
              theme === "dark" ? "bg-gray-800" : "bg-white"
            } rounded-lg shadow-xl max-w-md w-full p-6`}
          >
            <h3
              className={`text-lg font-semibold mb-2 ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Delete Companions
            </h3>
            <p
              className={`text-sm mb-4 ${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Unchecking "Allow Companions" will delete all {companions.length}{" "}
              existing companion{companions.length !== 1 ? "s" : ""}. This
              action cannot be undone.
            </p>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={handleCancelDeleteCompanions}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDeleteCompanions}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete All Companions"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Companion Addition Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
          <div
            className={`${
              theme === "dark" ? "bg-gray-800" : "bg-white"
            } rounded-lg shadow-xl max-w-md w-full p-6`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Confirm Companion Addition
              </h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className={`p-1 rounded transition-colors ${
                  theme === "dark"
                    ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                    : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <div>
                    <p
                      className={`text-sm font-medium ${
                        theme === "dark"
                          ? "text-gray-200"
                          : "text-gray-800"
                      }`}
                    >
                      You are about to {isImporting ? "import" : "add"}{" "}
                      {pendingCompanionsCount} companion
                      {pendingCompanionsCount !== 1 ? "s" : ""}.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={
                  isImporting ? handleConfirmImport : handleConfirmSave
                }
                disabled={loading}
              >
                {loading
                  ? isImporting
                    ? "Importing..."
                    : "Saving..."
                  : `Confirm & ${
                      isImporting ? "Import" : "Add"
                    } ${pendingCompanionsCount} Companion${
                      pendingCompanionsCount !== 1 ? "s" : ""
                    }`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};
